import React, {
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Button,
    Card,
    ColorPicker,
    Form,
    InputNumber,
    Select,
    Space,
    Spin,
    Tabs,
    Typography,
    Upload,
    message,
} from 'antd';
import { Rnd } from 'react-rnd';
import * as adminApi from 'js/utils/adminApi';
import {
    ColorType,
    IEvent,
    ITemplateBarcode,
    ITemplateText,
    ITicketTemplate,
    PositioningType,
} from 'store/slices/types';

const { Title, Text } = Typography;

const positioningOptions = [
    { value: 'center', label: 'center' },
    { value: 'left', label: 'left' },
    { value: 'right', label: 'right' },
];

const rgbToHex = (c: ColorType): string =>
    `#${c.map(v => v.toString(16).padStart(2, '0')).join('')}`;

const hexToRgb = (hex: string): ColorType => {
    const v = hex.replace('#', '');
    return [
        parseInt(v.slice(0, 2), 16),
        parseInt(v.slice(2, 4), 16),
        parseInt(v.slice(4, 6), 16),
    ];
};

const fontFileName = (path: string | null): string | undefined => {
    if (!path) return undefined;
    const parts = path.split(/[\\/]/);
    return parts[parts.length - 1];
};

// Convert center/left/right native coords to display top-left in pixels
function nativeToDisplay(
    nativeX: number,
    nativeY: number,
    ptype: PositioningType,
    displayWidth: number,
    displayHeight: number,
    scale: number
): { x: number; y: number } {
    const x = nativeX * scale;
    const y = nativeY * scale;
    if (ptype === 'center') {
        return { x: x - displayWidth / 2, y: y - displayHeight / 2 };
    }
    if (ptype === 'right') {
        return { x: x - displayWidth, y };
    }
    return { x, y };
}

// Inverse — given display top-left, get native anchor for the given ptype
function displayToNative(
    displayX: number,
    displayY: number,
    ptype: PositioningType,
    displayWidth: number,
    displayHeight: number,
    scale: number
): { x: number; y: number } {
    if (ptype === 'center') {
        return {
            x: (displayX + displayWidth / 2) / scale,
            y: (displayY + displayHeight / 2) / scale,
        };
    }
    if (ptype === 'right') {
        return {
            x: (displayX + displayWidth) / scale,
            y: displayY / scale,
        };
    }
    return { x: displayX / scale, y: displayY / scale };
}

// Rough estimate: text box width is text.size * 5 (chars wide)
const textBoxNativeSize = (text: ITemplateText): { w: number; h: number } => ({
    w: Math.max(text.size * 5, 120),
    h: Math.max(text.size * 1.2, 24),
});

interface CanvasEditorProps {
    template: ITicketTemplate;
    previewUrl: string | null;
    previewLoading: boolean;
    onTextPatch: (text: ITemplateText, patch: Partial<ITemplateText>) => void;
    onBarcodePatch: (
        barcode: ITemplateBarcode,
        patch: Partial<ITemplateBarcode>
    ) => void;
    onSelect: (kind: 'text' | 'barcode', id: number) => void;
    activeId: number | null;
    activeKind: 'text' | 'barcode' | null;
}

function CanvasEditor({
    template,
    previewUrl,
    previewLoading,
    onTextPatch,
    onBarcodePatch,
    onSelect,
    activeId,
    activeKind,
}: CanvasEditorProps): ReactElement {
    const imgRef = useRef<HTMLImageElement>(null);
    const [size, setSize] = useState<{
        natW: number;
        natH: number;
        dispW: number;
        dispH: number;
    } | null>(null);

    const recompute = useCallback((): void => {
        const img = imgRef.current;
        if (!img || !img.naturalWidth) return;
        setSize({
            natW: img.naturalWidth,
            natH: img.naturalHeight,
            dispW: img.clientWidth,
            dispH: img.clientHeight,
        });
    }, []);

    useEffect(() => {
        recompute();
        const handler = (): void => recompute();
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, [recompute, previewUrl]);

    if (previewLoading) {
        return (
            <div style={{ padding: 48 }}>
                <Spin />
            </div>
        );
    }
    if (!previewUrl) {
        return (
            <div style={{ padding: 48 }}>
                <Text type="secondary">Нет preview</Text>
            </div>
        );
    }

    const scale = size ? size.dispW / size.natW : 1;

    return (
        <div
            style={{
                position: 'relative',
                display: 'inline-block',
                lineHeight: 0,
            }}
        >
            <img
                ref={imgRef}
                src={previewUrl}
                alt="preview"
                onLoad={recompute}
                style={{
                    maxWidth: 600,
                    maxHeight: 800,
                    display: 'block',
                }}
            />
            {size &&
                template.texts.map((t, idx) => {
                    const native = textBoxNativeSize(t);
                    const dispW = native.w * scale;
                    const dispH = native.h * scale;
                    const ptype = t.position[2];
                    const { x, y } = nativeToDisplay(
                        t.position[0],
                        t.position[1],
                        ptype,
                        dispW,
                        dispH,
                        scale
                    );
                    const isActive = activeKind === 'text' && activeId === t.id;
                    return (
                        <Rnd
                            key={`text-${t.id}`}
                            bounds="parent"
                            size={{ width: dispW, height: dispH }}
                            position={{ x, y }}
                            onMouseDown={(): void => onSelect('text', t.id)}
                            onDragStop={(_, d): void => {
                                const next = displayToNative(
                                    d.x,
                                    d.y,
                                    ptype,
                                    dispW,
                                    dispH,
                                    scale
                                );
                                onTextPatch(t, {
                                    position: [
                                        Math.round(next.x),
                                        Math.round(next.y),
                                        ptype,
                                    ],
                                });
                            }}
                            onResizeStop={(_, __, ref, ___, pos): void => {
                                const newDispH = parseFloat(ref.style.height);
                                const newDispW = parseFloat(ref.style.width);
                                const newSize = Math.max(
                                    8,
                                    Math.round(newDispH / scale / 1.2)
                                );
                                const next = displayToNative(
                                    pos.x,
                                    pos.y,
                                    ptype,
                                    newDispW,
                                    newDispH,
                                    scale
                                );
                                onTextPatch(t, {
                                    size: newSize,
                                    position: [
                                        Math.round(next.x),
                                        Math.round(next.y),
                                        ptype,
                                    ],
                                });
                            }}
                            style={{
                                border: isActive
                                    ? '2px solid #1677ff'
                                    : '1px dashed rgba(22,119,255,0.6)',
                                background: 'rgba(22,119,255,0.08)',
                                boxSizing: 'border-box',
                            }}
                        >
                            <div
                                style={{
                                    color: '#1677ff',
                                    fontSize: 11,
                                    padding: 2,
                                    pointerEvents: 'none',
                                    lineHeight: '1em',
                                }}
                            >
                                T#{idx + 1}
                            </div>
                        </Rnd>
                    );
                })}
            {size && template.barcode && (
                <Rnd
                    key={`barcode-${template.barcode.id}`}
                    bounds="parent"
                    size={{
                        width: template.barcode.size[0] * scale,
                        height: template.barcode.size[1] * scale,
                    }}
                    position={nativeToDisplay(
                        template.barcode.position[0],
                        template.barcode.position[1],
                        template.barcode.position[2],
                        template.barcode.size[0] * scale,
                        template.barcode.size[1] * scale,
                        scale
                    )}
                    onMouseDown={(): void => {
                        if (template.barcode) {
                            onSelect('barcode', template.barcode.id);
                        }
                    }}
                    onDragStop={(_, d): void => {
                        if (!template.barcode) return;
                        const bw = template.barcode.size[0] * scale;
                        const bh = template.barcode.size[1] * scale;
                        const next = displayToNative(
                            d.x,
                            d.y,
                            template.barcode.position[2],
                            bw,
                            bh,
                            scale
                        );
                        onBarcodePatch(template.barcode, {
                            position: [
                                Math.round(next.x),
                                Math.round(next.y),
                                template.barcode.position[2],
                            ],
                        });
                    }}
                    onResizeStop={(_, __, ref, ___, pos): void => {
                        if (!template.barcode) return;
                        const newDispW = parseFloat(ref.style.width);
                        const newDispH = parseFloat(ref.style.height);
                        const newNatW = Math.round(newDispW / scale);
                        const newNatH = Math.round(newDispH / scale);
                        const next = displayToNative(
                            pos.x,
                            pos.y,
                            template.barcode.position[2],
                            newDispW,
                            newDispH,
                            scale
                        );
                        onBarcodePatch(template.barcode, {
                            size: [newNatW, newNatH],
                            position: [
                                Math.round(next.x),
                                Math.round(next.y),
                                template.barcode.position[2],
                            ],
                        });
                    }}
                    style={{
                        border:
                            activeKind === 'barcode'
                                ? '2px solid #fa8c16'
                                : '1px dashed rgba(250,140,22,0.7)',
                        background: 'rgba(250,140,22,0.10)',
                        boxSizing: 'border-box',
                    }}
                >
                    <div
                        style={{
                            color: '#fa8c16',
                            fontSize: 11,
                            padding: 2,
                            pointerEvents: 'none',
                            lineHeight: '1em',
                        }}
                    >
                        Barcode
                    </div>
                </Rnd>
            )}
        </div>
    );
}

function TextRow({
    text,
    fonts,
    onPatch,
}: {
    text: ITemplateText;
    fonts: string[];
    onPatch: (p: Partial<ITemplateText>) => Promise<void>;
}): ReactElement {
    const [form] = Form.useForm();
    useEffect(() => {
        form.setFieldsValue({
            size: text.size,
            color: rgbToHex(text.color),
            x: text.position[0],
            y: text.position[1],
            ptype: text.position[2],
            rotation: text.rotation,
            font: fontFileName(text.font),
        });
    }, [text, form]);

    const onBlurSave = async (): Promise<void> => {
        const v = form.getFieldsValue();
        await onPatch({
            size: v.size,
            color: hexToRgb(v.color?.toHexString?.() ?? v.color),
            position: [v.x, v.y, v.ptype as PositioningType],
            rotation: v.rotation,
        });
    };

    const onFontChange = async (font: string): Promise<void> => {
        await onPatch({ font });
    };

    return (
        <Form
            form={form}
            layout="vertical"
            size="small"
            onBlur={(): void => {
                onBlurSave().catch(() => undefined);
            }}
        >
            <Space wrap>
                <Form.Item name="size" label="Размер">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="color" label="Цвет">
                    <ColorPicker />
                </Form.Item>
                <Form.Item name="x" label="X">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="y" label="Y">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="ptype" label="Позиционир.">
                    <Select
                        style={{ width: 120 }}
                        options={positioningOptions}
                    />
                </Form.Item>
                <Form.Item name="rotation" label="Поворот">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="font" label="Шрифт">
                    <Select
                        style={{ width: 200 }}
                        options={fonts.map(f => ({ value: f, label: f }))}
                        onChange={(value): void => {
                            onFontChange(value).catch(() => undefined);
                        }}
                    />
                </Form.Item>
            </Space>
        </Form>
    );
}

function BarcodeRow({
    barcode,
    onPatch,
}: {
    barcode: ITemplateBarcode;
    onPatch: (p: Partial<ITemplateBarcode>) => Promise<void>;
}): ReactElement {
    const [form] = Form.useForm();
    useEffect(() => {
        form.setFieldsValue({
            type: barcode.type,
            w: barcode.size[0],
            h: barcode.size[1],
            x: barcode.position[0],
            y: barcode.position[1],
            ptype: barcode.position[2],
            rotation: barcode.rotation,
            top: barcode.bbox[0],
            right: barcode.bbox[1],
            bottom: barcode.bbox[2],
            left: barcode.bbox[3],
        });
    }, [barcode, form]);

    const onBlurSave = async (): Promise<void> => {
        const v = form.getFieldsValue();
        await onPatch({
            type: v.type,
            size: [v.w, v.h],
            position: [v.x, v.y, v.ptype as PositioningType],
            rotation: v.rotation,
            bbox: [v.top, v.right, v.bottom, v.left],
        });
    };

    return (
        <Form
            form={form}
            layout="vertical"
            size="small"
            onBlur={(): void => {
                onBlurSave().catch(() => undefined);
            }}
        >
            <Space wrap>
                <Form.Item name="type" label="Тип" style={{ width: 140 }}>
                    <Select
                        options={[
                            { value: 'code128', label: 'code128' },
                            { value: 'ean13', label: 'ean13' },
                        ]}
                    />
                </Form.Item>
                <Form.Item name="w" label="Ширина">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="h" label="Высота">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="x" label="X">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="y" label="Y">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="ptype" label="Позиционир.">
                    <Select
                        style={{ width: 120 }}
                        options={positioningOptions}
                    />
                </Form.Item>
                <Form.Item name="rotation" label="Поворот">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="top" label="bbox.top">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="right" label="bbox.right">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="bottom" label="bbox.bottom">
                    <InputNumber />
                </Form.Item>
                <Form.Item name="left" label="bbox.left">
                    <InputNumber />
                </Form.Item>
            </Space>
        </Form>
    );
}

export default function TicketEditorPage(): ReactElement {
    const [events, setEvents] = useState<IEvent[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);
    const [template, setTemplate] = useState<ITicketTemplate | null>(null);
    const [fonts, setFonts] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [activeId, setActiveId] = useState<number | null>(null);
    const [activeKind, setActiveKind] = useState<'text' | 'barcode' | null>(
        null
    );

    const reloadTemplate = useCallback(async (eId: number): Promise<void> => {
        const list = await adminApi.listEvents();
        const ev = list.find(e => e.id === eId);
        if (!ev || !ev.ticket_template_id) {
            setTemplate(null);
            return;
        }
        const tpl = await adminApi.getTemplate(ev.ticket_template_id);
        setTemplate(tpl);
    }, []);

    useEffect(() => {
        Promise.all([adminApi.listEvents(), adminApi.listFonts()])
            .then(([eventsList, fontsList]) => {
                setEvents(eventsList);
                setFonts(fontsList);
                if (eventsList.length) {
                    const first = eventsList[0];
                    setEventId(first.id);
                    if (first.ticket_template_id) {
                        return adminApi
                            .getTemplate(first.ticket_template_id)
                            .then(setTemplate);
                    }
                }
                return undefined;
            })
            .finally(() => setLoading(false));
    }, []);

    const refreshPreview = useCallback(async (): Promise<void> => {
        if (!template) return;
        setPreviewLoading(true);
        try {
            const blob = await adminApi.previewTemplate(template.id, {});
            setPreviewUrl(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return URL.createObjectURL(blob);
            });
        } catch {
            message.error('Не удалось отрендерить preview');
        } finally {
            setPreviewLoading(false);
        }
    }, [template]);

    useEffect(() => {
        if (template) refreshPreview().catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template?.id]);

    const onTextPatch = async (
        text: ITemplateText,
        patch: Partial<ITemplateText>
    ): Promise<void> => {
        const payload: adminApi.TextPatchPayload = {};
        if (patch.size !== undefined) payload.size = patch.size;
        if (patch.color !== undefined) payload.color = patch.color;
        if (patch.position !== undefined) payload.position = patch.position;
        if (patch.rotation !== undefined) payload.rotation = patch.rotation;
        if (patch.font !== undefined && patch.font !== null) {
            payload.font = patch.font;
        }
        const updated = await adminApi.patchTemplateText(text.id, payload);
        setTemplate(prev =>
            prev
                ? {
                      ...prev,
                      texts: prev.texts.map(t =>
                          t.id === updated.id ? { ...t, ...updated } : t
                      ),
                  }
                : prev
        );
        refreshPreview().catch(() => undefined);
    };

    const onBarcodePatch = async (
        barcode: ITemplateBarcode,
        patch: Partial<ITemplateBarcode>
    ): Promise<void> => {
        const updated = await adminApi.patchTemplateBarcode(barcode.id, patch);
        setTemplate(prev => {
            if (!prev || !prev.barcode) return prev;
            return { ...prev, barcode: { ...prev.barcode, ...updated } };
        });
        refreshPreview().catch(() => undefined);
    };

    const onBackgroundUpload = async (file: File): Promise<boolean> => {
        if (!template) return false;
        const updated = await adminApi.uploadTemplateBackground(
            template.id,
            file
        );
        setTemplate(updated);
        message.success('Фон обновлён');
        return false;
    };

    const onFontUpload = async (file: File): Promise<boolean> => {
        const updated = await adminApi.uploadFont(file);
        setFonts(updated);
        message.success('Шрифт загружен');
        return false;
    };

    if (loading) return <Spin />;

    if (!template) {
        return (
            <div>
                <Title level={3}>Редактор билета</Title>
                <Space style={{ marginBottom: 16 }}>
                    <span>Event:</span>
                    <Select
                        style={{ width: 280 }}
                        value={eventId ?? undefined}
                        onChange={(v): void => {
                            setEventId(v);
                            reloadTemplate(v).catch(() => undefined);
                        }}
                        options={events.map(e => ({
                            value: e.id,
                            label: e.name,
                        }))}
                    />
                </Space>
                <Text type="secondary">
                    Шаблон билета для этого event ещё не создан.
                </Text>
            </div>
        );
    }

    const currentBarcode = template.barcode;

    return (
        <div>
            <Title level={3}>Редактор билета</Title>
            <Space style={{ marginBottom: 16 }}>
                <span>Event:</span>
                <Select
                    style={{ width: 280 }}
                    value={eventId ?? undefined}
                    onChange={(v): void => {
                        setEventId(v);
                        reloadTemplate(v).catch(() => undefined);
                    }}
                    options={events.map(e => ({ value: e.id, label: e.name }))}
                />
                <Upload
                    showUploadList={false}
                    beforeUpload={(file): Promise<boolean> =>
                        onBackgroundUpload(file as File)
                    }
                    accept="image/png,image/jpeg"
                >
                    <Button>Загрузить фон</Button>
                </Upload>
                <Upload
                    showUploadList={false}
                    beforeUpload={(file): Promise<boolean> =>
                        onFontUpload(file as File)
                    }
                    accept=".otf,.ttf"
                >
                    <Button>Загрузить шрифт</Button>
                </Upload>
                <Button onClick={refreshPreview} loading={previewLoading}>
                    Обновить preview
                </Button>
            </Space>

            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                <Card
                    title="Preview (можно тащить и тянуть)"
                    style={{ flex: '0 0 auto' }}
                    styles={{ body: { padding: 0, background: '#f0f2f5' } }}
                >
                    <CanvasEditor
                        template={template}
                        previewUrl={previewUrl}
                        previewLoading={previewLoading}
                        onTextPatch={(t, p): void => {
                            onTextPatch(t, p).catch(() => undefined);
                        }}
                        onBarcodePatch={(b, p): void => {
                            onBarcodePatch(b, p).catch(() => undefined);
                        }}
                        onSelect={(kind, id): void => {
                            setActiveKind(kind);
                            setActiveId(id);
                        }}
                        activeId={activeId}
                        activeKind={activeKind}
                    />
                </Card>

                <Card title="Элементы" style={{ flex: 1, minWidth: 360 }}>
                    <Tabs
                        items={[
                            {
                                key: 'texts',
                                label: 'Тексты',
                                children: (
                                    <Space
                                        direction="vertical"
                                        style={{ width: '100%' }}
                                    >
                                        {template.texts.map((t, idx) => {
                                            const isActive =
                                                activeKind === 'text' &&
                                                activeId === t.id;
                                            return (
                                                <Card
                                                    key={t.id}
                                                    size="small"
                                                    title={`Текст #${idx + 1}`}
                                                    onClick={(): void => {
                                                        setActiveKind('text');
                                                        setActiveId(t.id);
                                                    }}
                                                    style={{
                                                        borderColor: isActive
                                                            ? '#1677ff'
                                                            : undefined,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <TextRow
                                                        text={t}
                                                        fonts={fonts}
                                                        onPatch={(
                                                            patch
                                                        ): Promise<void> =>
                                                            onTextPatch(
                                                                t,
                                                                patch
                                                            )
                                                        }
                                                    />
                                                </Card>
                                            );
                                        })}
                                    </Space>
                                ),
                            },
                            {
                                key: 'barcode',
                                label: 'Штрихкод',
                                children: currentBarcode ? (
                                    <BarcodeRow
                                        barcode={currentBarcode}
                                        onPatch={(patch): Promise<void> =>
                                            onBarcodePatch(
                                                currentBarcode,
                                                patch
                                            )
                                        }
                                    />
                                ) : (
                                    <Text type="secondary">
                                        Штрихкод не настроен
                                    </Text>
                                ),
                            },
                        ]}
                    />
                </Card>
            </div>
        </div>
    );
}
